/**
 * DocumentDetailRoute — the routed single-document overlay (`/documents/:docId`).
 *
 * A thin connective wrapper around the presentational `DocumentDetail`. It reads
 * the doc id from the route, fetches just that one document with
 * `useVaultDocument`, maps the raw node onto the presentational vault shape, and
 * resolves its space + access grants from shared context. Closing pushes back to
 * the Documents grid.
 */
import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { LoadingIndicator } from "@jsq/ds/core";
import {
  apiVaultDocumentToVaultDocument,
  buildAttributeValueInputs,
  useUpdateVaultDocument,
  useUploadVaultDocumentVersion,
  useVaultDocument,
  useVaultDocumentDownload,
  useVaultDocumentPreview,
  type AttributeValueSpec,
} from "#common/graphql/Document";
import { DocumentDetail } from "#components/DocumentDetail";
import * as styles from "../styles_vanilla.css";
import { documentDetailSaveErrorMessage } from "../utils";
import { useVaultData } from "../VaultProvider";
import type { GlobalAttribute, Space } from "#common/types/vault";
import type { AttributeEdit } from "#components/DocumentDetail/types";

const labelsBySlugFromEdits = (
  edits: AttributeEdit[],
  globalLib: GlobalAttribute[],
  space: Space | null,
): Record<string, string> => {
  const out: Record<string, string> = {};
  edits.forEach((edit) => {
    if (edit.scope === "global") {
      const attr = globalLib.find((g) => g.key === edit.key);
      if (attr) out[attr.key] = attr.name;
      return;
    }
    const local = space?.locals.find((l) => l.name === edit.key);
    if (local) out[local.slug ?? local.name] = local.name;
  });
  return out;
};

export const DocumentDetailRoute = ({ basePath }: { basePath: string }) => {
  const history = useHistory();
  const { search } = useLocation();
  const { docId } = useParams<{ docId: string }>();
  const {
    spaces,
    globalLib,
    globalIdByKey,
    access,
    reference,
    lookups,
    showToast,
    hasCapability,
  } = useVaultData();

  const { node, loading, refetch } = useVaultDocument(docId);
  const doc = node ? apiVaultDocumentToVaultDocument(node, lookups) : null;
  const space = doc ? (spaces.find((s) => s.id === doc.space) ?? null) : null;

  const { updateVaultDocument, loading: savingAttrs } = useUpdateVaultDocument();
  const { uploadVersion, uploading: savingVersion } = useUploadVaultDocumentVersion();

  // Only PDFs are rendered inline, so don't spend a round-trip (or buffer the
  // bytes) for anything else — those go straight to the download action.
  const isPdf = (doc?.file ?? "").toLowerCase() === "pdf";
  const {
    previewUrl,
    loading: previewLoading,
    error: previewError,
    tooLarge,
  } = useVaultDocumentPreview({ publicRefId: docId, skip: !doc || !isPdf });

  const { mintDownloadUrl } = useVaultDocumentDownload();
  // The presigned URL is attachment-disposition, so a plain navigation IS the
  // download — no extra anchor plumbing needed.
  const downloadDocument = () => {
    void (async () => {
      const url = await mintDownloadUrl(docId);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    })();
  };

  // Same as `downloadDocument`, pinned to one version. `n` is the presentational
  // label ("v3"); the leading "v" plus one or more digits is the `ssbVersionNumber`
  // the query wants — anything else (a stray "v", a decimal) is not a real version.
  const downloadVersion = (n: string) => {
    const match = /^v(\d+)$/i.exec(n);
    if (!match) return;
    const versionNumber = Number(match[1]);
    void (async () => {
      const url = await mintDownloadUrl(docId, versionNumber);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    })();
  };

  // Persist the edit form: resolve each edit's backend attributeId + dataType,
  // then route it through the shared value-input builder (id-shaped attrs ->
  // valueIds, scalars -> the typed column). Unresolvable attributes are dropped.
  // `clear` rides along so a deliberately emptied scalar reaches the wire as an
  // empty column — the mutation upserts, so a dropped empty keeps the old value.
  const saveAttrs = async (id: string, edits: AttributeEdit[]) => {
    const specs: AttributeValueSpec[] = [];
    edits.forEach((edit) => {
      if (edit.scope === "global") {
        const attributeId = globalIdByKey[edit.key];
        const attr = globalLib.find((g) => g.key === edit.key);
        if (attributeId && attr) {
          specs.push({
            attributeId,
            dataType: attr.dataType,
            value: edit.value,
            ids: edit.ids,
            clear: edit.clear,
          });
        }
        return;
      }
      const local = space?.locals.find((l) => l.name === edit.key);
      if (local?.id) {
        specs.push({
          attributeId: local.id,
          dataType: local.dataType,
          value: edit.value,
          ids: edit.ids,
          clear: edit.clear,
        });
      }
    });
    try {
      await updateVaultDocument({
        publicRefId: id,
        attributeValues: buildAttributeValueInputs(specs),
      });
    } catch (error) {
      showToast(
        documentDetailSaveErrorMessage(error, labelsBySlugFromEdits(edits, globalLib, space)),
      );
      throw error;
    }
    // Toast fires immediately after the write succeeds so it doesn't feel laggy.
    showToast("Changes saved");
    try {
      // AWAITED: `DocumentDetail` clears its staged edits when this promise settles,
      // so resolving before the refetch lands would blank the draft while the grid
      // still showed the pre-save values.
      await refetch();
    } catch {
      // Write succeeded; refetch is best-effort UI freshness — don't surface as a
      // save failure.
    }
  };

  // `changeNote` travels to `finalizeVaultDocument`, not `replaceVaultDocument`
  // (see `useUploadVaultDocumentVersion`) — Mode A (new bytes, always the case
  // from a file picker) ignores the note on the replace call itself. An empty
  // note is omitted rather than sent as "": the field is meant to record what
  // changed, and an empty string on the wire isn't meaningfully different from
  // not sending it.
  const uploadNewVersion = async (id: string, version: { note: string; file: File }) => {
    await uploadVersion({
      publicRefId: id,
      file: version.file,
      changeNote: version.note || undefined,
    });
    // AWAITED for the same reason as `saveAttrs`: the compose modal only closes
    // once this settles, and the refetch has to land before it does so the
    // reopened Version history tab shows the new version rather than stale data.
    await refetch();
  };

  if (loading) {
    return (
      <div
        className={styles.content.wide}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <LoadingIndicator size="medium" variant="primary" />
      </div>
    );
  }

  return (
    <DocumentDetail
      document={doc}
      space={space}
      globalLib={globalLib}
      access={doc ? (access[doc.id] ?? []) : []}
      reference={reference}
      previewUrl={previewUrl}
      previewLoading={previewLoading}
      previewError={
        tooLarge
          ? "This document is too large to preview in the browser."
          : previewError?.message
      }
      // DV-693 §5/§2: an absent callback is `DocumentDetail`'s own disabled /
      // read-only state, so gating capability here needs no change to that
      // component — see its own doc comments on each of these props.
      onDownload={
        hasCapability("can_download_doc_vault_documents") ? downloadDocument : undefined
      }
      onDownloadVersion={
        hasCapability("can_download_doc_vault_documents") ? downloadVersion : undefined
      }
      onClose={() => history.push({ pathname: `${basePath}/documents`, search })}
      onSaveAttrs={hasCapability("can_classify_doc_vault_documents") ? saveAttrs : undefined}
      savingAttrs={savingAttrs}
      onNewVersion={
        hasCapability("can_replace_doc_vault_document_version") ? uploadNewVersion : undefined
      }
      savingVersion={savingVersion}
      // The routed shell always sits inside the app's ApolloProvider, so the live
      // Client / Entity search components can be used for those attributes.
      referenceSearch
    />
  );
};
